/**
 * src/modules/legacy-dashboard-helpers.ts — Sprint 20.2 Etap 6 (2026-05-02 SESSION 14).
 *
 * Pure dashboard/orders rendering helpers — wyciągnięte z app.js.
 *   - _buildSparklineSVG (KPI compare sparklines)
 *   - _fmtCompareValue (PLN / int formatter)
 *   - _calcCompareDelta (% change)
 *   - _getGlobalComparePeriod / _setGlobalComparePeriod (localStorage)
 *   - _buildPaymentBadge / _buildShipmentBadge (HTML status badges)
 *
 * Strangler: window expose tylko gdy app.js IIFE nie zdefiniowała.
 *
 * NIE ekstrahuję `renderDashboard`, `renderKPIs`, `_buildEnhancedKpiCard` —
 * coupled z DOM render i state, defer do incremental rewrite.
 */

import { normalizeShipmentStatus } from './orders/shipment-status';

declare const escapeHtml: (s: any) => string;
declare const escapeAttr: (s: any) => string;

/** Formatowanie liczb pulpitu — JEDNO źródło prawdy (AUDYT 18-D2 #2).
 *
 * Kafelek potrafił pokazać obok siebie „4 811,01" (formatowane w Pythonie) i
 * „4811,01" (Intl bez grupowania — `pl-PL` domyślnie NIE grupuje liczb
 * czterocyfrowych). Dlatego: `useGrouping: 'always'` i ŻADNEJ delegacji do
 * `window._fmtMoneyPL` z legacy-utils — pulpit liczy sobie sam, tą samą funkcją
 * dla wartości dzisiejszej i porównawczej.
 */
export function _fmtMoneyDashboard(n: any, decimals?: number): string {
  const d = decimals == null ? 2 : decimals;
  const liczba = Number(n) || 0;
  try {
    return new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
      useGrouping: 'always',
    } as any).format(liczba);
  } catch (_e) {
    // Starsze silniki bez `useGrouping: 'always'` — grupujemy ręcznie.
    const [calosc, reszta] = liczba.toFixed(d).split('.');
    const znak = calosc.startsWith('-') ? '-' : '';
    const cyfry = znak ? calosc.slice(1) : calosc;
    const zgrupowane = cyfry.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return znak + zgrupowane + (reszta ? ',' + reszta : '');
  }
}

export function _buildSparklineSVG(values: number[], color: string, partialLast?: boolean): string {
  if (!Array.isArray(values) || values.length < 2) return '';
  const w = 120;
  const h = 28;
  const pad = 1;
  let max = Math.max.apply(null, values);
  const min = Math.min.apply(null, values);
  if (max === min) max = min + 1;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / (max - min)) * (h - pad * 2);
    return x.toFixed(1) + ',' + y.toFixed(1);
  });
  // AUDYT 18-D2 #11: ostatni punkt to DZIŚ, czyli doba w toku — linia zawsze
  // nurkowała na końcu i wyglądała jak załamanie sprzedaży. Odcinek dzisiejszy
  // rysujemy przerywanie i jaśniej, a obszar kończymy na dniu poprzednim.
  const pelne = partialLast ? pts.slice(0, -1) : pts;
  const areaPts = pelne.length >= 2 ? pelne : pts;
  const ostatniX = areaPts[areaPts.length - 1].split(',')[0];
  const areaPath = 'M' + areaPts[0] + ' L' + areaPts.join(' L') + ' L' + ostatniX + ',' + h + ' L' + pad + ',' + h + ' Z';
  const linePath = 'M' + areaPts.join(' L');
  const ogon = (partialLast && pts.length >= 2)
    ? '<path d="M' + pts[pts.length - 2] + ' L' + pts[pts.length - 1] + '" fill="none" stroke="' + color +
      '" stroke-width="1.5" stroke-dasharray="2 2" opacity="0.55" stroke-linecap="round"/>'
    : '';
  return (
    '<svg class="kpi-sparkline" width="' + w + '" height="' + h + '" viewBox="0 0 ' + w + ' ' + h +
    '" style="display:block;width:100%;max-width:' + w + 'px">' +
    '<path d="' + areaPath + '" fill="' + color + '" opacity="0.15"/>' +
    '<path d="' + linePath + '" fill="none" stroke="' + color + '" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    ogon +
    '</svg>'
  );
}

export function _fmtCompareValue(v: any, format: string): string {
  // `null`/`undefined` = NIE MA dnia porównawczego. Kiedyś backend słał wtedy 0,
  // a kafelek pokazywał twarde „0" — nieodróżnialne od realnego braku sprzedaży.
  if (v === null || v === undefined || v === '') return '—';
  if (format === 'pln') return _fmtMoneyDashboard(v) + ' PLN';
  // Liczby całkowite też grupujemy — „1 248 zamówień" czyta się od razu, „1248" nie.
  return _fmtMoneyDashboard(Math.round(Number(v) || 0), 0);
}

export function _calcCompareDelta(todayVal: any, compareVal: any): { value: string; type: 'up' | 'down' | 'neutral'; pct: number } {
  // Brak dnia porównawczego (null) = brak procentu; „0%" byłoby zmyśleniem.
  if (compareVal === null || compareVal === undefined || compareVal === '') return { value: '', type: 'neutral', pct: 0 };
  const t = Number(todayVal) || 0;
  const c = Number(compareVal) || 0;
  if (c === 0) return { value: '', type: 'neutral', pct: 0 };
  const pct = Math.round((t - c) / c * 100);
  if (pct > 0) return { value: '+' + pct + '%', type: 'up', pct };
  if (pct < 0) return { value: pct + '%', type: 'down', pct };
  return { value: '0%', type: 'neutral', pct: 0 };
}

export function _getGlobalComparePeriod(): string {
  try {
    return localStorage.getItem('kpi_compare_period_global') || 'yesterday';
  } catch (_e) {
    return 'yesterday';
  }
}

export function _setGlobalComparePeriod(period: string): void {
  try { localStorage.setItem('kpi_compare_period_global', period); } catch (_e) { /* ignore */ }
}

function _esc(s: any): string {
  if (typeof escapeHtml === 'function') return escapeHtml(s);
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
}
function _escAttr(s: any): string {
  if (typeof escapeAttr === 'function') return escapeAttr(s);
  return _esc(s);
}

export function _buildPaymentBadge(order: any): string {
  const w = window as any;
  const pay = order.payment || {};
  const payStatus = String(typeof pay.status === 'object' ? (pay.status.key || '') : (pay.status || '')).toLowerCase();
  const treeMap = (w.__sbStateExt && w.__sbStateExt._paymentStatusTreeMap) || null;
  const info = treeMap ? treeMap[payStatus] : null;
  const color = info ? info.color : (
    payStatus === 'paid' ? '#22c55e' :
    payStatus === 'pending' ? '#f59e0b' :
    payStatus === 'cod' ? '#3b82f6' :
    payStatus === 'cod_paid' ? '#14b8a6' :
    payStatus === 'refunded' ? '#ef4444' : '#6b7280'
  );
  const rawLabel = info ? info.name : (
    payStatus === 'paid' ? 'Opłacone' :
    payStatus === 'pending' ? 'Oczekuje' :
    payStatus === 'cod' ? 'Pobranie' :
    payStatus === 'cod_paid' ? 'Opłacone (pobranie)' : payStatus
  );
  const i18n = w.__sbI18n;
  const label = i18n && i18n.tStatus ? i18n.tStatus('payment', payStatus, rawLabel) : rawLabel;
  return (
    '<span data-status-cat="payment" data-status-key="' + _escAttr(payStatus) +
    '" style="display:inline-flex;align-items:center;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:' +
    color + '18;color:' + color + ';border:1px solid ' + color + '30">' + _esc(label) + '</span>'
  );
}

export function _buildShipmentBadge(order: any): string {
  const w = window as any;
  const shipStatus = normalizeShipmentStatus(order.shipment_status);
  const treeMap = (w.__sbStateExt && w.__sbStateExt._shipmentStatusTreeMap) || null;
  const info = treeMap ? treeMap[shipStatus] : null;
  const defMap: Record<string, { name: string; color: string }> = {
    no_shipment: { name: 'Brak przesyłki', color: '#9ca3af' },
    label_generated: { name: 'Etykieta wygenerowana', color: '#6366f1' },
    ready_for_pickup: { name: 'Gotowa do odbioru', color: '#22c55e' },
    created: { name: 'Utworzona', color: '#3b82f6' },
    dispatched: { name: 'Nadana', color: '#8b5cf6' },
    in_transit: { name: 'W transporcie', color: '#f59e0b' },
    issue: { name: 'Problem z doręczeniem', color: '#f59e0b' },
    delivered: { name: 'Dostarczona', color: '#22c55e' },
    returned: { name: 'Zwrócona', color: '#f97316' },
    cancelled: { name: 'Anulowana', color: '#ef4444' },
  };
  const def = defMap[shipStatus] || { name: shipStatus, color: '#6b7280' };
  const color = info ? info.color : def.color;
  const rawLabel = info ? info.name : def.name;
  const i18nSh = w.__sbI18n;
  const label = i18nSh && i18nSh.tStatus ? i18nSh.tStatus('shipment', shipStatus, rawLabel) : rawLabel;
  return (
    '<span data-shipment-order-id="' + _escAttr(String(order.id || '')) + '" data-status-cat="shipment" data-status-key="' + _escAttr(shipStatus) +
    '" style="display:inline-flex;align-items:center;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:' +
    color + '18;color:' + color + ';border:1px solid ' + color + '30">' + _esc(label) + '</span>'
  );
}

// Strangler exports
if (typeof window !== 'undefined') {
  const w = window as any;
  if (typeof w._buildSparklineSVG !== 'function') w._buildSparklineSVG = _buildSparklineSVG;
  if (typeof w._fmtCompareValue !== 'function') w._fmtCompareValue = _fmtCompareValue;
  if (typeof w._fmtMoneyDashboard !== 'function') w._fmtMoneyDashboard = _fmtMoneyDashboard;
  if (typeof w._calcCompareDelta !== 'function') w._calcCompareDelta = _calcCompareDelta;
  if (typeof w._getGlobalComparePeriod !== 'function') w._getGlobalComparePeriod = _getGlobalComparePeriod;
  if (typeof w._setGlobalComparePeriod !== 'function') w._setGlobalComparePeriod = _setGlobalComparePeriod;
  if (typeof w._buildPaymentBadge !== 'function') w._buildPaymentBadge = _buildPaymentBadge;
  if (typeof w._buildShipmentBadge !== 'function') w._buildShipmentBadge = _buildShipmentBadge;
}
